import React, { useMemo } from 'react';
import type { ChangeEvent } from 'react';

import classnames from 'classnames';

import type { ColorChangeHandler } from 'react-color';

import Tooltip from 'rc-tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBold,
  faItalic,
  faUnderline,
  faStrikethrough,
  faAlignLeft,
  faAlignRight,
  faAlignCenter,
} from '@fortawesome/pro-solid-svg-icons';

import { FlexBox } from '@eltoro-ui/components';

import ColorPicker from 'Components/CanvasEditor/components/ColorPicker';

import type { ButtonElement, CanvasElement, TextElement } from 'Components/CanvasEditor/types';

interface TextContentProps {
  selectedElement: TextElement | ButtonElement;
  onElementChange: (props: Partial<CanvasElement>, id: string, saveToHistory?: boolean) => void;
}

export default function TextContent({ selectedElement, onElementChange }: TextContentProps) {
  const isTextSelected = selectedElement.type === 'text';

  const toggleBold = () => {
    if (!selectedElement || !isTextSelected) return;
    const currentStyle = selectedElement.fontStyle || '';

    if (currentStyle.includes('bold')) {
      onElementChange(
        {
          fontStyle: currentStyle.replace('bold', '').trim(),
        },
        selectedElement.id,
        true
      );
    } else {
      onElementChange(
        {
          fontStyle: `${currentStyle} bold`.trim(),
        },
        selectedElement.id,
        true
      );
    }
  };

  const toggleItalic = () => {
    if (!selectedElement || !isTextSelected) return;
    const currentStyle = selectedElement.fontStyle || '';

    if (currentStyle.includes('italic')) {
      onElementChange(
        {
          fontStyle: currentStyle.replace('italic', '').trim(),
        },
        selectedElement.id,
        true
      );
    } else {
      onElementChange(
        {
          fontStyle: `${currentStyle} italic`.trim(),
        },
        selectedElement.id,
        true
      );
    }
  };

  const toggleUnderline = () => {
    if (!selectedElement || !isTextSelected) return;
    const currentDecoration = selectedElement.textDecoration ?? '';

    if (currentDecoration.includes('underline')) {
      onElementChange(
        {
          textDecoration: currentDecoration.replace('underline', '').trim(),
        },
        selectedElement.id,
        true
      );
    } else {
      onElementChange(
        {
          textDecoration: `${currentDecoration} underline`.trim(),
        },
        selectedElement.id,
        true
      );
    }
  };

  const toggleStrikeThrough = () => {
    if (!selectedElement || !isTextSelected) return;
    const currentDecoration = selectedElement.textDecoration ?? '';

    if (currentDecoration.includes('line-through')) {
      onElementChange(
        {
          textDecoration: currentDecoration.replace('line-through', '').trim(),
        },
        selectedElement.id,
        true
      );
    } else {
      onElementChange(
        {
          textDecoration: `${currentDecoration} line-through`.trim(),
        },
        selectedElement.id,
        true
      );
    }
  };

  const setTextAlign = (align: string) => {
    if (!selectedElement || !isTextSelected) return;
    onElementChange({ align }, selectedElement.id, true);
  };

  const color = useMemo(() => {
    if (selectedElement?.type === 'text' && 'background' in selectedElement)
      return selectedElement.background as string;
    return '';
  }, [selectedElement]);

  const onChangeColorPicker: ColorChangeHandler = ({ hex }) => {
    if (!selectedElement || !('background' in selectedElement)) return;

    onElementChange({ background: hex }, selectedElement.id, true);
  };

  const onChangeFontFamily = (e: ChangeEvent<HTMLSelectElement>) =>
    onElementChange({ fontFamily: e.target.value }, selectedElement.id, true);

  const onChangeFontSize = (e: ChangeEvent<HTMLInputElement>) =>
    onElementChange({ fontSize: parseInt(e.target.value, 10) }, selectedElement.id, true);

  const onChangeCornerRadius = (e: ChangeEvent<HTMLInputElement>) =>
    onElementChange({ cornerRadius: parseInt(e.target.value, 10) }, selectedElement.id, true);

  return (
    <>
      <select
        className="font-select"
        value={selectedElement.fontFamily}
        onChange={onChangeFontFamily}
      >
        <option value="Arial">Arial</option>
        <option value="Verdana">Verdana</option>
        <option value="Times New Roman">Times New Roman</option>
        <option value="Courier New">Courier New</option>
        <option value="Georgia">Georgia</option>
        <option value="Open Sans">Open Sans</option>
        <option value="Roboto">Roboto</option>
      </select>
      <input
        className="font-size-input"
        type="number"
        value={selectedElement.fontSize}
        onChange={onChangeFontSize}
      />
      <FlexBox alignItems="center" gap="0.25rem" UNSAFE_className="text-options-wrappper">
        <button
          className={classnames(
            'text-option',
            selectedElement.fontStyle?.includes('bold') && 'text-option-active'
          )}
          onClick={toggleBold}
        >
          <FontAwesomeIcon icon={faBold} />
        </button>
        <button
          className={classnames(
            'text-option',
            selectedElement.fontStyle?.includes('italic') && 'text-option-active'
          )}
          onClick={toggleItalic}
        >
          <FontAwesomeIcon icon={faItalic} />
        </button>
        <button
          className={classnames(
            'text-option',
            selectedElement.textDecoration?.includes('underline') && 'text-option-active'
          )}
          onClick={toggleUnderline}
        >
          <FontAwesomeIcon icon={faUnderline} />
        </button>
        <button
          className={classnames(
            'text-option',
            selectedElement.textDecoration?.includes('line-through') && 'text-option-active'
          )}
          onClick={toggleStrikeThrough}
        >
          <FontAwesomeIcon icon={faStrikethrough} />
        </button>
      </FlexBox>
      <FlexBox alignItems="center" gap="0.25rem" UNSAFE_className="text-options-wrappper">
        <button
          className={classnames(
            'text-option',
            selectedElement.align === 'left' && 'text-option-active'
          )}
          onClick={() => setTextAlign('left')}
        >
          <FontAwesomeIcon icon={faAlignLeft} />
        </button>
        <button
          className={classnames(
            'text-option',
            selectedElement.align === 'center' && 'text-option-active'
          )}
          onClick={() => setTextAlign('center')}
        >
          <FontAwesomeIcon icon={faAlignCenter} />
        </button>
        <button
          className={classnames(
            'text-option',
            selectedElement.align === 'right' && 'text-option-active'
          )}
          onClick={() => setTextAlign('right')}
        >
          <FontAwesomeIcon icon={faAlignRight} />
        </button>
      </FlexBox>
      {selectedElement?.type === 'text' && 'background' in selectedElement && (
        <Tooltip
          placement="bottom"
          showArrow={false}
          classNames={{ root: 'property-tooltip' }}
          trigger="click"
          overlay={
            <FlexBox flexDirection="column" gap="0.75rem">
              <div className="property-row">
                <div className="property-label">Corner Radius</div>
                <div className="property-value">
                  <input
                    className="font-size-input"
                    type="number"
                    value={selectedElement.cornerRadius as number}
                    onChange={onChangeCornerRadius}
                  />
                </div>
              </div>
              <div className="property-row">
                <div className="property-label">Background Color</div>
                <div className="property-value">
                  <ColorPicker
                    type="color"
                    selectedElement={selectedElement}
                    color={color}
                    onChangeColorPicker={onChangeColorPicker}
                  />
                </div>
              </div>
            </FlexBox>
          }
        >
          <span className="image-mask">Box Options</span>
        </Tooltip>
      )}
    </>
  );
}
